import { Injectable, inject, signal, WritableSignal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { ApolloClient, InMemoryCache, createHttpLink, from as apolloFrom } from '@apollo/client/core';
import gql from 'graphql-tag';
import { onError } from '@apollo/client/link/error';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';

export interface GraphQLQuery {
  query: string;
  variables?: Record<string, any>;
  operationName?: string;
}

export interface GraphQLResponse<T = any> {
  data?: T;
  errors?: Array<{ message: string; path?: string[]; extensions?: any }>;
}

@Injectable({ providedIn: 'root' })
export class GraphQLService {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly _client: WritableSignal<ApolloClient | null> = signal(null);
  private graphqlUrl = '/api/v1/graphql'; // Use proxy

  constructor() {
    this.initializeClient();
  }

  /**
   * Initialize Apollo Client
   */
  private initializeClient(): void {
    const httpLink = createHttpLink({
      uri: this.graphqlUrl,
      fetch: (uri, options) => {
        const token = this.authService.token();
        const headers = new Headers(options?.headers as HeadersInit);
        
        if (token) {
          headers.set('Authorization', `Bearer ${token}`);
        }
        
        // Add tenant ID if available
        const user = this.authService.user();
        if (user?.tenantId) {
          headers.set('x-tenant-id', user.tenantId);
        }

        return fetch(uri, {
          ...options,
          headers,
        });
      },
    });

    // Error link
    const errorLink = onError((errorContext) => {
      const graphQLErrors = (errorContext as any).graphQLErrors;
      const networkError = (errorContext as any).networkError;
      
      if (graphQLErrors) {
        graphQLErrors.forEach((error: any) => {
          console.error(
            `[GraphQL error]: Message: ${error.message}, Location: ${error.locations}, Path: ${error.path}`,
            error.extensions
          );
        });
      }

      if (networkError) {
        console.error(`[Network error]: ${networkError}`);
        
        // Handle 401 unauthorized
        if ('statusCode' in networkError && networkError.statusCode === 401) {
          this.authService.logout();
        }
      }
    });

    const client = new ApolloClient({
      link: apolloFrom([errorLink, httpLink]),
      cache: new InMemoryCache({
        typePolicies: {},
      }),
      defaultOptions: {
        watchQuery: {
          errorPolicy: 'all',
          fetchPolicy: 'cache-and-network',
        },
        query: {
          errorPolicy: 'all',
          fetchPolicy: 'cache-first',
        },
      },
    });

    this._client.set(client);
  }

  /**
   * Execute a GraphQL query
   */
  query<T = any>(query: string, variables?: Record<string, any>): Observable<GraphQLResponse<T>> {
    const client = this._client();
    if (!client) {
      return throwError(() => new Error('GraphQL client not initialized'));
    }

    return from(
      client.query<T>({
        query: gql`
          ${query}
        `,
        variables,
        fetchPolicy: 'cache-first',
        errorPolicy: 'all',
      })
    ).pipe(
      map((result) => ({
        data: result.data,
        errors: result.error ? [result.error] : undefined,
      })),
      catchError((error) => {
        console.error('GraphQL query error:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Execute a GraphQL mutation
   */
  mutate<T = any>(mutation: string, variables?: Record<string, any>): Observable<GraphQLResponse<T>> {
    const client = this._client();
    if (!client) {
      return throwError(() => new Error('GraphQL client not initialized'));
    }

    return from(
      client.mutate<T>({
        mutation: gql`
          ${mutation}
        `,
        variables,
        errorPolicy: 'all',
      })
    ).pipe(
      map((result) => ({
        data: result.data,
        errors: result.error ? [result.error] : undefined,
      })),
      catchError((error) => {
        console.error('GraphQL mutation error:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Execute a GraphQL subscription (if WebSocket is configured)
   */
  subscribe<T = any>(subscription: string, variables?: Record<string, any>): Observable<GraphQLResponse<T>> {
    const client = this._client();
    if (!client) {
      return throwError(() => new Error('GraphQL client not initialized'));
    }

    // Note: Subscriptions require WebSocket setup
    // This is a placeholder for future implementation
    return throwError(() => new Error('GraphQL subscriptions not yet implemented'));
  }

  /**
   * Reset cache
   */
  resetCache(): void {
    const client = this._client();
    if (client) {
      client.cache.reset();
    }
  }

  /**
   * Get Apollo Client instance
   */
  getClient(): ApolloClient | null {
    return this._client();
  }
}

